import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Chip,
  Alert
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Gargalo } from '../../services/relatorioService';

interface GargalosIdentificadosProps {
  dados: Gargalo[];
}

const GargalosIdentificados: React.FC<GargalosIdentificadosProps> = ({ dados }) => {
  const obterIconeSeveridade = (severidade: string) => {
    switch (severidade) {
      case 'critica':
        return <ErrorIcon color="error" />;
      case 'alta':
        return <WarningIcon color="warning" />;
      case 'media':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon color="disabled" />;
    }
  };

  const obterCorSeveridade = (severidade: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (severidade) {
      case 'critica':
        return 'error';
      case 'alta':
        return 'warning';
      case 'media':
        return 'info';
      default:
        return 'default';
    }
  };

  const obterLabelSeveridade = (severidade: string): string => {
    switch (severidade) {
      case 'critica':
        return 'CRÍTICO';
      case 'alta':
        return 'ALTO';
      case 'media':
        return 'MÉDIO';
      default:
        return 'BAIXO';
    }
  };

  const obterTipoLabel = (tipo: string): string => {
    switch (tipo) {
      case 'status':
        return '📊 Status';
      case 'departamento':
        return '🏢 Departamento';
      case 'usuario':
        return '👤 Usuário';
      default:
        return tipo;
    }
  };

  if (!dados || dados.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h6">Gargalos Identificados</Typography>
          </Box>
          <Alert severity="success">
            <strong>Excelente!</strong> Nenhum gargalo crítico identificado no momento! 🎉
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Agrupar por severidade
  const criticos = dados.filter(g => g.severidade === 'critica');
  const altos = dados.filter(g => g.severidade === 'alta');
  const medios = dados.filter(g => g.severidade === 'media');
  const baixos = dados.filter(g => g.severidade === 'baixa');

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ReportProblemIcon sx={{ mr: 1, color: 'error.main' }} />
          <Typography variant="h6">🚧 Gargalos Identificados</Typography>
        </Box>
        
        {criticos.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Atenção Urgente!</strong> {criticos.length} gargalo(s) crítico(s) detectado(s)
          </Alert>
        )}

        <List>
          {dados.map((gargalo, index) => (
            <ListItem 
              key={index}
              sx={{ 
                borderLeft: 3, 
                borderColor: `${obterCorSeveridade(gargalo.severidade)}.main`,
                mb: 1,
                bgcolor: 'background.paper',
                borderRadius: 1
              }}
            >
              <ListItemIcon>
                {obterIconeSeveridade(gargalo.severidade)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" fontWeight="medium">
                      {gargalo.nome}
                    </Typography>
                    <Chip 
                      label={obterLabelSeveridade(gargalo.severidade)}
                      size="small"
                      color={obterCorSeveridade(gargalo.severidade)}
                    />
                    <Chip 
                      label={obterTipoLabel(gargalo.tipo)}
                      size="small"
                      variant="outlined"
                    />
                    <Chip 
                      label={`${gargalo.quantidade} tarefas`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {gargalo.descricao}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        {/* Resumo por severidade */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {criticos.length > 0 && (
            <Chip 
              label={`${criticos.length} Crítico(s)`}
              color="error"
              size="small"
            />
          )}
          {altos.length > 0 && (
            <Chip 
              label={`${altos.length} Alto(s)`}
              color="warning"
              size="small"
            />
          )}
          {medios.length > 0 && (
            <Chip 
              label={`${medios.length} Médio(s)`}
              color="info"
              size="small"
            />
          )}
          {baixos.length > 0 && (
            <Chip 
              label={`${baixos.length} Baixo(s)`}
              color="default"
              size="small"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default GargalosIdentificados;
